import { LocationDescriptorObject } from 'history';

interface KeyValueStore {
  set: (provider: string, clickId: string, expiresInDays: number) => void;
  get: (provider: string) => string | undefined;
}

interface AffiliationSource {
  provider: string;
  clickId: string;
}

interface Tracker {
  key: string;
  getSource: (location: LocationDescriptorObject) => AffiliationSource;
  shouldBeTracked: (
    location: LocationDescriptorObject,
    keyValueStore: KeyValueStore,
  ) => boolean;
  serialise: (source: AffiliationSource) => string;
  deserialise: (value: string | undefined) => AffiliationSource | undefined;
  expireInDays: number;
}

// List of trackers. New trackers can be added in this list.
// Tackers are ordered based on priority:
// tracker with lower index has higher prioirity
const trackers: Tracker[] = [
  {
    key: 'google',
    getSource: (location: LocationDescriptorObject) => ({
      provider: 'google',
      clickId: location.query.gclid,
    }),
    shouldBeTracked: (location: LocationDescriptorObject) =>
      !!location.query.gclid,
    serialise: (source: AffiliationSource) => source.clickId,
    deserialise: (value: string) => ({ provider: 'google', clickId: value }),
    expireInDays: 90,
  },
  {
    key: 'facebook',
    getSource: (location: LocationDescriptorObject) => ({
      provider: 'facebook',
      clickId: location.query.fbclid,
    }),
    shouldBeTracked: (location: LocationDescriptorObject) =>
      !!location.query.fbclid,
    serialise: (source: AffiliationSource) => source.clickId,
    deserialise: (value: string) => ({ provider: 'facebook', clickId: value }),
    expireInDays: 90,
  },
  {
    key: 'nestpick',
    getSource: (location: LocationDescriptorObject) => ({
      provider: 'nestpick',
      clickId: location.query.nestpick_transaction_id,
    }),
    shouldBeTracked: (location: LocationDescriptorObject) =>
      !!location.query.nestpick_transaction_id,
    serialise: (source: AffiliationSource) => source.clickId,
    deserialise: (value: string) => ({ provider: 'nestpick', clickId: value }),
    expireInDays: 30,
  },
  {
    key: 'hometogo',
    getSource: (location: LocationDescriptorObject) => ({
      provider: 'hometogo',
      clickId: location.query.utm_term,
    }),
    shouldBeTracked: (location: LocationDescriptorObject) =>
      location.query.utm_source === 'hometogo' && !!location.query.utm_term,
    serialise: (source: AffiliationSource) => source.clickId,
    deserialise: (value: string) => ({ provider: 'hometogo', clickId: value }),
    expireInDays: 30,
  },
  {
    key: 'affiliate',
    getSource: (location: LocationDescriptorObject) => ({
      provider: location.query.affiliate_partner_id,
      clickId: location.query.affiliate_click_id,
    }),
    shouldBeTracked: (
      location: LocationDescriptorObject,
      kvstore: KeyValueStore,
    ) =>
      // we use a very rudimental first touch attribution strategy
      // basically if no attribution is tracked we add attribution
      // othwerwis we ignre it.
      kvstore.get('affiliate') === undefined &&
      !!location.query.affiliate_partner_id &&
      !!location.query.affiliate_click_id,

    serialise: (source: AffiliationSource) => JSON.stringify(source),
    deserialise: (value: string) => {
      try {
        return JSON.parse(value);
      } catch {
        // explicitly ignoring parsing errors to avoid crashes
      }
    },
    expireInDays: 30,
  },
];

export default function newAffilationTracker(keyValueStore: KeyValueStore) {
  const getSource = (): AffiliationSource | undefined => {
    const sources = trackers
      .map(tracker => {
        const value = keyValueStore.get(tracker.key);

        return value ? tracker.deserialise(value) : undefined;
      })
      .filter(source => source !== undefined);

    return sources[0] as AffiliationSource | undefined;
  };

  const trackSource = (location: LocationDescriptorObject): void => {
    // on the safe side ignore with invalid location
    if (!location || !location.query) {
      return;
    }

    trackers.forEach((tracker: Tracker) => {
      if (tracker.shouldBeTracked(location, keyValueStore)) {
        keyValueStore.set(
          tracker.key,
          tracker.serialise(tracker.getSource(location)),
          tracker.expireInDays,
        );
      }
    });
  };

  return {
    getSource,
    trackSource,
  };
}
